import i18n from '@/plugins/vue-i18n';
import { defineStore, storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth.store';
import { withheldProjectRoles } from '@/common/utils/role-flag-gate.utils';
import { useSiteStore } from '@/stores/site.store';
import { useToastStore } from '@/stores/utils/toast.store';
import { showClientRoleSwitchToast } from '@/common/utils/user-role-toast.utils';
import {
  getProjectsRoles,
  getSitesRoles,
  updateProjectClientRole,
  updateSiteClientRole,
} from '@/api/roles.service';
import {
  BOQRoles,
  GetRolesParams,
  OrgRoles,
  ProjRoles,
  Role,
  SiteRoles,
  UpdateProjectRoleParams,
} from '@/types/api/roles.types';

const { t } = i18n.global;

/**
 * Roles withheld by a feature flag that is currently off. Empty today — no project role
 * is flag-gated — but the lookup stays wired so the next gated role needs no new plumbing.
 *
 * Context: CASL is gated upstream in `permission.store`, but the legacy adapter and the
 * direct role checks below read the auth store straight — so they must consult the same
 * withheld set, or a flag-gated role would stay live for them if the CASL rework flag were
 * ever flipped back to the legacy engine (PE-589).
 */
const withheldRoles = (): ReadonlySet<string> => withheldProjectRoles({});

/** Drops any queried role that a flag is currently withholding. */
const grantableRoles = <T extends string>(roles: T[]): T[] => {
  const withheld = withheldRoles();

  return withheld.size === 0 ? roles : roles.filter((role) => !withheld.has(role));
};

// Centralize all role constants
export const ROLE_CONSTANTS = {
  ADMIN_ROLES: [OrgRoles.ORG_ADMIN, OrgRoles.ORG_OWN],

  NON_CLIENT_PROFESSIONAL_ROLES: [
    ProjRoles.PROJ_MNGR,
    // Context: PROJ_PROC_OFF is a project-scoped procurement officer, so it reads projects,
    // sites, orders, vendors and the org exactly as the other non-client roles do. The
    // navigation and read gates are built on this list, so membership here is what actually
    // opens those surfaces for the role (PE-588).
    ProjRoles.PROJ_PROC_OFF,
    SiteRoles.SITE_ADMIN,
    SiteRoles.SITE_FLD_USR,
    SiteRoles.SITE_FIN_OFF,
    SiteRoles.SITE_PROC_OFF,
  ],

  CLIENT_ROLES: [
    ProjRoles.PROJ_CLI_APRV,
    ProjRoles.PROJ_CLI_BASE,
    SiteRoles.SITE_CLI_APRV,
    SiteRoles.SITE_CLI_BASE,
  ],

  PROFESSIONAL_ROLES: [ProjRoles.PROJ_PROF, SiteRoles.SITE_PROF],

  // Add display name maps for all role types
  DISPLAY_NAMES: {
    project: {
      [ProjRoles.PROJ_MNGR]: () => t('roles.project.manager'),
      [ProjRoles.PROJ_MEMB]: () => t('roles.project.member'),
      [ProjRoles.PROJ_PROF]: () => t('roles.project.professional'),
      [ProjRoles.PROJ_CLI_BASE]: () => t('roles.project.client'),
      [ProjRoles.PROJ_CLI_APRV]: () => t('roles.project.client'),
    },
    site: {
      [SiteRoles.SITE_ADMIN]: () => t('roles.site.admin'),
      [SiteRoles.SITE_MEMBER]: () => t('roles.site.member'),
      [SiteRoles.SITE_PROC_OFF]: () => t('roles.site.procOff'),
      [SiteRoles.SITE_FIN_OFF]: () => t('roles.site.finOff'),
      [SiteRoles.SITE_FLD_USR]: () => t('roles.site.fieldUser'),
      [SiteRoles.SITE_PROF]: () => t('roles.site.professional'),
      [SiteRoles.SITE_CLI_BASE]: () => t('roles.site.client'),
      [SiteRoles.SITE_CLI_APRV]: () => t('roles.site.clientWithApproval'),
    },
    org: {
      [OrgRoles.ORG_ADMIN]: () => t('roles.org.admin'),
      [OrgRoles.ORG_OWN]: () => t('roles.org.owner'),
      [OrgRoles.ORG_FO]: () => t('roles.org.finOff'),
      [OrgRoles.ORG_FA]: () => t('roles.org.finAdmin'),
      [OrgRoles.ORG_MEMB]: () => t('roles.org.member'),
    },
    boq: {
      [BOQRoles.BOQ_OWN]: () => t('roles.boq.owner'),
      [BOQRoles.BOQ_ADMIN]: () => t('roles.boq.admin'),
      [BOQRoles.BOQ_CAT_ADMIN]: () => t('roles.boq.catalogAdmin'),
    },
  },
};

export const useRolesStore = defineStore('roles', {
  state: () => ({
    projectRoles: JSON.parse(localStorage.getItem('projectRoles') || '[]') as Role[],
    siteRoles: JSON.parse(localStorage.getItem('siteRoles') || '[]') as Role[],
    orgRoles: [
      {
        id: 1,
        code: OrgRoles.ORG_OWN,
        name: t('roles.org.owner'),
      },
      {
        id: 2,
        code: OrgRoles.ORG_ADMIN,
        name: t('roles.org.admin'),
      },
      {
        id: 3,
        code: OrgRoles.ORG_FO,
        name: t('roles.org.finOff'),
      },
      {
        id: 4,
        code: OrgRoles.ORG_FA,
        name: t('roles.org.finAdmin'),
      },
      {
        id: 5,
        code: OrgRoles.ORG_MEMB,
        name: t('roles.org.member'),
      },
    ] as unknown as Role[],
  }),

  actions: {
    async getSiteRoles(params?: GetRolesParams) {
      const response = await getSitesRoles(params);
      this.siteRoles = response.data.data;
      localStorage.setItem('siteRoles', JSON.stringify(this.siteRoles));
    },

    async getProjectRoles(params?: GetRolesParams) {
      const response = await getProjectsRoles(params);
      this.projectRoles = response.data.data;
      localStorage.setItem('projectRoles', JSON.stringify(this.projectRoles));
    },

    /** Atomically changes a site-user-role entry's role. No toast — callers decide the wording. */
    async updateSiteUserRole(siteUserRoleId: string, targetRole: Role): Promise<boolean> {
      const siteStore = useSiteStore();

      const response = await updateSiteClientRole(siteUserRoleId, { siteRoleId: targetRole.id });
      if (response.status === 200) {
        siteStore.updateUserRoleCode(siteUserRoleId, targetRole);
        return true;
      }
      return false;
    },

    /** Client-role swap wrapper around `updateSiteUserRole` — adds the swap-direction toast. */
    async updateSiteClientRole(siteUserRoleId: string, targetRole: Role, email: string) {
      const success = await this.updateSiteUserRole(siteUserRoleId, targetRole);
      if (success) {
        showClientRoleSwitchToast(email, targetRole.code === SiteRoles.SITE_CLI_APRV);
      }
    },

    async updateProjectClientRole(projectUserRoleId: string, data: UpdateProjectRoleParams) {
      const toastStore = useToastStore();

      const response = await updateProjectClientRole(projectUserRoleId, data);
      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t('users.toasts.changeClientRole.title'),
          t('users.toasts.changeClientRole.content'),
        );
      }
    },

    /**
     * Check if the current user has specific roles using direct enums
     * @param type The type of entity to check roles for
     * @param rolesToCheck The roles to check for (array of enum values)
     * @param projectId The ID of the project (required for 'project' type)
     * @param siteId The ID of the site (required for 'site' type)
     * @returns Boolean indicating if the user has any of the specified roles
     */
    checkDirectRoles(
      type: 'organisation' | 'project' | 'site' | 'any-site' | 'any-project' | 'anywhere',
      requestedRoles: (OrgRoles | ProjRoles | SiteRoles)[],
      projectId?: string,
      siteId?: string,
    ): boolean {
      const rolesToCheck = grantableRoles(requestedRoles);
      if (rolesToCheck.length === 0) return false;

      const authStore = useAuthStore();
      const currentUser = authStore.loggedInUser;
      const selectedOrgId = this.selectedOrgId;

      if (!selectedOrgId) return false;

      // Find the matching organisation from user's roles
      const matchingOrg = currentUser.roles?.organisations?.find((org) => org.id === selectedOrgId);
      if (!matchingOrg) return false;

      // Get organisation roles, projects, and sites
      const matchingOrgProjects = matchingOrg.projects || [];
      const matchingOrgSites = matchingOrg.sites || [];
      const matchingOrgRoles = matchingOrg.roles || [];

      // Check specific entity types using switch statement
      switch (type) {
        case 'organisation': {
          // Check if any of the required organisation roles are present
          return rolesToCheck.some((role) => matchingOrgRoles.includes(role as OrgRoles));
        }

        case 'site': {
          // Site ID is required for specific site role checks
          if (!siteId) return false;

          // Find the site with the given ID
          const matchingSite = matchingOrgSites.find((site) => site.id === siteId);
          if (!matchingSite) return false;

          // Check if any of the required site roles are present
          return rolesToCheck.some((role) => matchingSite.roles.includes(role as SiteRoles));
        }

        case 'project': {
          // Project ID is required for specific project role checks
          if (!projectId) return false;

          // Find the project with the given ID
          const matchingProject = matchingOrgProjects.find((project) => project.id === projectId);
          if (!matchingProject) return false;

          // Check if any of the required project roles are present
          return rolesToCheck.some((role) => matchingProject.roles.includes(role as ProjRoles));
        }

        case 'any-site': {
          // Check for roles across all sites
          return matchingOrgSites.some((site) =>
            rolesToCheck.some((role) => site.roles.includes(role as SiteRoles)),
          );
        }

        case 'any-project': {
          // Check for roles across all projects
          return matchingOrgProjects.some((project) =>
            rolesToCheck.some((role) => project.roles.includes(role as ProjRoles)),
          );
        }

        case 'anywhere': {
          // Check for roles anywhere in the organisation (org roles, any project, any site)
          // Check org roles
          if (rolesToCheck.some((role) => matchingOrgRoles.includes(role as OrgRoles))) {
            return true;
          }

          // Check any project roles
          if (
            matchingOrgProjects.some((project) =>
              rolesToCheck.some((role) => project.roles.includes(role as ProjRoles)),
            )
          ) {
            return true;
          }

          // Check any site roles
          if (
            matchingOrgSites.some((site) =>
              rolesToCheck.some((role) => site.roles.includes(role as SiteRoles)),
            )
          ) {
            return true;
          }

          return false;
        }

        default: {
          return false;
        }
      }
    },

    /**
     * Check if user has BOQ level permissions
     */
    hasBOQPermissions(permissions: BOQRoles[]): boolean {
      const authStore = useAuthStore();
      const { loggedInUser } = storeToRefs(authStore);

      return permissions.some((code) =>
        loggedInUser.value?.permissions?.some((permission) => permission.code === code),
      );
    },

    /**
     * Check if user has org level permissions
     */
    hasOrgPermissions(permissions: OrgRoles[]): boolean {
      const authStore = useAuthStore();
      const { loggedInUser } = storeToRefs(authStore);

      // Read the active org's full role set (the same source checkDirectRoles
      // and CASL use) so multi-role users are matched on any held role. This is
      // flag-independent — roles.organisations[].roles is always an array, with
      // one entry when the BE is single-role and N when multi-role is enabled.
      // Returns false — never throws — when roles.organisations is absent (cold
      // start, before getUserPermissions resolves).
      const org = loggedInUser.value?.roles?.organisations?.find(
        (o) => o.id === this.selectedOrgId,
      );
      return !!org && permissions.some((role) => org.roles.includes(role));
    },

    /**
     * Check if user has specific permission at site or project level
     */
    hasLowerLevelPermissions(type: 'project' | 'site', id: string, permission: string): boolean {
      if (withheldRoles().has(permission)) return false;

      const authStore = useAuthStore();
      const { loggedInUser } = storeToRefs(authStore);

      if (!loggedInUser.value) return false;

      const { roles } = loggedInUser.value;
      const org = roles?.organisations.find((org) => org.id === this.selectedOrgId);

      const permissions = type === 'project' ? org?.projects : org?.sites;
      const permissionToFind = permissions?.find((item) => item.id === id);

      return permissionToFind ? permissionToFind.roles.includes(permission) : false;
    },

    /**
     * Check if user has specific permission at org level for site or project
     */
    /**
     * Whether the user runs this project's day-to-day operations — BOQ authoring and approval,
     * orders, claims, returns, cart, vendors, items, cashflow, compliance and bank draws.
     *
     * Context: PROJ_PROC_OFF is SITE_PROC_OFF at project scope, so the Core API grants it the
     * same operational arms as PROJ_MNGR — everything except project and site administration,
     * payment-request writes and accounts payable. Ask this rather than naming PROJ_MNGR, so a
     * control gated on "runs this project" does not silently exclude the next project-scoped
     * role. A gate that genuinely means ADMINISTRATION should keep naming PROJ_MNGR directly.
     */
    hasProjectOperationsRole(projectId: string): boolean {
      return (
        this.hasLowerLevelPermissions('project', projectId, ProjRoles.PROJ_MNGR) ||
        this.hasLowerLevelPermissions('project', projectId, ProjRoles.PROJ_PROC_OFF)
      );
    },

    /** `hasProjectOperationsRole` for a control with no project in context — the role held on any. */
    hasOrgProjectOperationsRole(): boolean {
      return (
        this.hasOrgLowerLevelPermissions('project', ProjRoles.PROJ_MNGR) ||
        this.hasOrgLowerLevelPermissions('project', ProjRoles.PROJ_PROC_OFF)
      );
    },

    hasOrgLowerLevelPermissions(type: 'project' | 'site', permission: string): boolean {
      if (withheldRoles().has(permission)) return false;

      const authStore = useAuthStore();
      const { loggedInUser } = storeToRefs(authStore);

      if (!loggedInUser.value) return false;

      const { roles } = loggedInUser.value;
      const org = roles?.organisations.find((org) => org.id === this.selectedOrgId);

      if (type === 'site' && org) {
        return org.sites.some((site) => site.roles.includes(permission));
      } else if (type === 'project' && org) {
        return org.projects.some((project) => project.roles.includes(permission));
      }

      return false;
    },
  },

  getters: {
    // Get the selected organization ID
    selectedOrgId(): string | undefined {
      const authStore = useAuthStore();
      return authStore.loggedInUser?.selectedOrg?.id;
    },

    // Return project roles filtering out for specified roles
    filteredProjectRoles(): Role[] {
      const excludedRoles = new Set([ProjRoles.PROJ_CLI_APRV, ProjRoles.PROJ_MEMB]);
      return this.projectRoles.filter((role) => !excludedRoles.has(role.code as ProjRoles));
    },

    // Return site roles filtering out for specified roles
    filteredSiteRoles(): Role[] {
      const excludedRoles = new Set([SiteRoles.SITE_MEMBER]);
      return this.siteRoles.filter((role) => !excludedRoles.has(role.code as SiteRoles));
    },

    // Look up a site role by its code from the loaded catalogue
    getSiteRoleByCode() {
      return (code: SiteRoles): Role | undefined =>
        this.siteRoles.find((role) => role.code === code);
    },

    // Permission utility
    permissions() {
      const check = (
        type: 'organisation' | 'project' | 'site' | 'any-site' | 'any-project' | 'anywhere',
        roles: (OrgRoles | ProjRoles | SiteRoles)[],
        projectId?: string,
        siteId?: string,
      ) => useRolesStore().checkDirectRoles(type, roles, projectId, siteId);

      return {
        project: {
          isManager: (projectId: string): boolean =>
            check('project', [ProjRoles.PROJ_MNGR], projectId),
          isMember: (projectId: string): boolean =>
            check('project', [ProjRoles.PROJ_MEMB], projectId),
          isProfessional: (projectId: string): boolean =>
            check('project', [ProjRoles.PROJ_PROF], projectId),
          isProcurementOfficer: (projectId: string): boolean =>
            check('project', [ProjRoles.PROJ_PROC_OFF], projectId),
          isClient: (projectId: string): boolean =>
            check('project', [ProjRoles.PROJ_CLI_BASE], projectId),
          isClientWithApproval: (projectId: string): boolean =>
            check('project', [ProjRoles.PROJ_CLI_APRV], projectId),
          hasRoleInAnyProject: (roles: ProjRoles[]): boolean => check('any-project', roles),
          hasNonClientProfessionalRole: (projectId: string): boolean =>
            check(
              'project',
              ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES.filter((role) =>
                Object.values(ProjRoles).includes(role as ProjRoles),
              ) as ProjRoles[],
              projectId,
            ),
        },
        site: {
          isAdmin: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_ADMIN], undefined, siteId),
          isProcurementOfficer: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_PROC_OFF], undefined, siteId),
          isFinanceOfficer: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_FIN_OFF], undefined, siteId),
          isFieldUser: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_FLD_USR], undefined, siteId),
          isProfessional: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_PROF], undefined, siteId),
          isClient: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_CLI_BASE], undefined, siteId),
          isClientWithApproval: (siteId: string): boolean =>
            check('site', [SiteRoles.SITE_CLI_APRV], undefined, siteId),
          hasRoleInAnySite: (roles: SiteRoles[]): boolean => check('any-site', roles),
          canViewSite: (siteId: string, projectId?: string): boolean =>
            check('organisation', [
              OrgRoles.ORG_ADMIN,
              OrgRoles.ORG_OWN,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]) ||
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check(
              'site',
              [
                SiteRoles.SITE_ADMIN,
                SiteRoles.SITE_FIN_OFF,
                SiteRoles.SITE_PROC_OFF,
                SiteRoles.SITE_FLD_USR,
                SiteRoles.SITE_PROF,
              ],
              undefined,
              siteId,
            ) ||
            (projectId
              ? check(
                  'project',
                  [ProjRoles.PROJ_MNGR, ProjRoles.PROJ_PROF, ProjRoles.PROJ_PROC_OFF],
                  projectId,
                )
              : check('any-project', [
                  ProjRoles.PROJ_MNGR,
                  ProjRoles.PROJ_PROF,
                  ProjRoles.PROJ_PROC_OFF,
                ])),
        },
        organisation: {
          isOwner: (): boolean => check('organisation', [OrgRoles.ORG_OWN]),
          isAdmin: (): boolean => check('organisation', [OrgRoles.ORG_ADMIN]),
          isFinanceOfficer: (): boolean => check('organisation', [OrgRoles.ORG_FO]),
          isFinanceAdmin: (): boolean => check('organisation', [OrgRoles.ORG_FA]),
          isMember: (): boolean => check('organisation', [OrgRoles.ORG_MEMB]),
          hasRoleAnywhereInOrganisation: (roles: (OrgRoles | ProjRoles | SiteRoles)[]): boolean =>
            check('anywhere', roles),
        },
        navigation: {
          // BOQ platform roles (BOQ_ADMIN/BOQ_OWN) are cross-org and see the staff nav in
          // every org (mirrors the CASL grants + the canViewSite precedent above). Checked via
          // hasBOQPermissions (org-independent) so a pure BOQ_ADMIN in a BOQ-only org still
          // gets the nav under the legacy permission system.
          canSeeDashboard: (): boolean =>
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check('anywhere', [...ROLE_CONSTANTS.ADMIN_ROLES, OrgRoles.ORG_FO, OrgRoles.ORG_FA]),
          canSeeClientDashboard: (): boolean =>
            check('anywhere', [
              ...ROLE_CONSTANTS.CLIENT_ROLES,
              ...ROLE_CONSTANTS.PROFESSIONAL_ROLES,
            ]),
          canSeeProjects: (): boolean =>
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check('anywhere', [
              ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
              ...ROLE_CONSTANTS.ADMIN_ROLES,
              ...ROLE_CONSTANTS.PROFESSIONAL_ROLES,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]),
          canSeeOrders: (): boolean =>
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check('anywhere', [
              ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
              ...ROLE_CONSTANTS.ADMIN_ROLES,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]),
          canSeeClaims: (): boolean =>
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check('anywhere', [
              ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
              ...ROLE_CONSTANTS.ADMIN_ROLES,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]),
          canSeeAccounts: (): boolean =>
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check('anywhere', [
              ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
              ...ROLE_CONSTANTS.ADMIN_ROLES,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]),
          canSeeVendors: (): boolean =>
            useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN]) ||
            check('anywhere', [
              ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
              ...ROLE_CONSTANTS.ADMIN_ROLES,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]),
          canSeeClientVariations: (): boolean =>
            check('anywhere', [
              ...ROLE_CONSTANTS.CLIENT_ROLES,
              ...ROLE_CONSTANTS.PROFESSIONAL_ROLES,
            ]),
          canSeeBOQVariations: (): boolean =>
            check('anywhere', [...ROLE_CONSTANTS.PROFESSIONAL_ROLES]),
          // Org settings / user-permission menu gate, shared by the layout shells
          // (DefaultLayout, BaseLayout, NavigationOnlyLayout). Org finance officers
          // (ORG_FO/ORG_FA) can view their own org settings even without manage-others
          // permission. Intentionally omits the BOQ-staff check the other nav gates
          // carry, preserving the historical per-layout settings-menu behaviour.
          canSeeSettings: (): boolean =>
            check('anywhere', [
              ...ROLE_CONSTANTS.NON_CLIENT_PROFESSIONAL_ROLES,
              ...ROLE_CONSTANTS.ADMIN_ROLES,
              OrgRoles.ORG_FO,
              OrgRoles.ORG_FA,
            ]),
        },
        boq: {
          canManageItems: (projectId: string, siteId: string): boolean =>
            useRolesStore().canManageBOQItems(projectId, siteId),
          canViewCostBOQ: (projectId: string, siteId: string): boolean =>
            useRolesStore().canViewCostBOQ(projectId, siteId),
          canViewSellingBOQ: (projectId: string, siteId: string): boolean =>
            useRolesStore().canViewCostBOQ(projectId, siteId) ||
            useRolesStore().permissions.site.isProfessional(siteId) ||
            useRolesStore().permissions.project.isProfessional(projectId),
        },
      };
    },
    /**
     * Checks if the user is an external user only (has only client and/or professional roles)
     */
    isExternalUserOnly(): boolean {
      // BOQ platform staff (BOQ_ADMIN/BOQ_OWN) are internal, cross-org users — never
      // "external only", even in an org where they hold no org/project/site role. Checked
      // via hasBOQPermissions (org-independent) so a pure BOQ_ADMIN isn't classed as external.
      if (useRolesStore().hasBOQPermissions([BOQRoles.BOQ_ADMIN, BOQRoles.BOQ_OWN])) {
        return false;
      }

      const authStore = useAuthStore();
      const currentUser = authStore.loggedInUser;

      const selectedOrgId = this.selectedOrgId;
      if (!selectedOrgId) return false;

      const matchingOrg = currentUser.roles?.organisations?.find((org) => org.id === selectedOrgId);
      if (!matchingOrg) return false;

      if (
        matchingOrg.roles.some(
          (role) =>
            role === OrgRoles.ORG_ADMIN ||
            role === OrgRoles.ORG_OWN ||
            role === OrgRoles.ORG_FO ||
            role === OrgRoles.ORG_FA,
        )
      ) {
        return false;
      }

      const allSiteRoles = matchingOrg.sites?.flatMap((site) => site.roles) || [];

      // Context: PROJ_PROC_OFF is an internal operational role, so a holder is not an external
      // user. Missing it here classified them as client/professional-only, which hid every
      // line-item action in the BOQ behind `!isProfessional` (PE-588). Routed through
      // checkDirectRoles rather than a raw includes() so the role stays withheld while its
      // flag is off — a raw list would make it live flag-off.
      const hasInternalProjectRoles = useRolesStore().checkDirectRoles('any-project', [
        ProjRoles.PROJ_MNGR,
        ProjRoles.PROJ_PROC_OFF,
      ]);

      const hasInternalSiteRoles = allSiteRoles.some(
        (role) =>
          role === SiteRoles.SITE_ADMIN ||
          role === SiteRoles.SITE_PROC_OFF ||
          role === SiteRoles.SITE_FIN_OFF ||
          role === SiteRoles.SITE_FLD_USR,
      );

      // Return true only if there are NO internal roles
      return !hasInternalProjectRoles && !hasInternalSiteRoles;
    },
    canManageBOQItems: () => {
      return (projectId: string, siteId: string): boolean => {
        const store = useRolesStore();

        const isBOQAdmin = store.hasBOQPermissions([BOQRoles.BOQ_ADMIN]);

        const isOrgAdmin = store.checkDirectRoles('organisation', [OrgRoles.ORG_ADMIN]);

        const isProjectManager = store.checkDirectRoles(
          'project',
          [ProjRoles.PROJ_MNGR, ProjRoles.PROJ_PROC_OFF],
          projectId,
        );

        const isSiteAdminOrStaff = store.checkDirectRoles(
          'site',
          [SiteRoles.SITE_ADMIN, SiteRoles.SITE_FLD_USR, SiteRoles.SITE_PROC_OFF],
          undefined,
          siteId,
        );

        return isBOQAdmin || isOrgAdmin || isProjectManager || isSiteAdminOrStaff;
      };
    },
    canViewCostBOQ: () => {
      return (projectId: string, siteId: string): boolean => {
        const store = useRolesStore();

        const isBOQAdminOrOwner =
          store.hasBOQPermissions([BOQRoles.BOQ_ADMIN]) ||
          store.hasBOQPermissions([BOQRoles.BOQ_OWN]);

        const isOrgAdminOrOwner =
          store.checkDirectRoles('organisation', [OrgRoles.ORG_ADMIN]) ||
          store.checkDirectRoles('organisation', [OrgRoles.ORG_OWN]);

        const isProjectManager = store.checkDirectRoles(
          'project',
          [ProjRoles.PROJ_MNGR, ProjRoles.PROJ_PROC_OFF],
          projectId,
        );

        const isSiteAdminOrStaff = store.checkDirectRoles(
          'site',
          [
            SiteRoles.SITE_ADMIN,
            SiteRoles.SITE_FLD_USR,
            SiteRoles.SITE_PROC_OFF,
            SiteRoles.SITE_FIN_OFF,
          ],
          undefined,
          siteId,
        );

        const isOrgFinance =
          store.checkDirectRoles('organisation', [OrgRoles.ORG_FO]) ||
          store.checkDirectRoles('organisation', [OrgRoles.ORG_FA]);

        return (
          isBOQAdminOrOwner ||
          isOrgAdminOrOwner ||
          isOrgFinance ||
          isProjectManager ||
          isSiteAdminOrStaff
        );
      };
    },
  },
});
